import { Grid, Stack } from '@mui/material';
import { useEventCallback } from '@mui/material/utils';
import { pick } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { LabeledInput } from '@work4all/components/lib/input/labeled-input';
import {
  EventType,
  sendAmplitudeData,
} from '@work4all/components/lib/utils/amplitude/amplitude';

import { useDataMutation } from '@work4all/data';

import { NumberingLogic } from '@work4all/models/lib/Classes/NumberingLogic.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { PathsOf } from '@work4all/utils/lib/paths-of/paths-of';

import { useFormContextPlus } from '../../../form-plus/use-form-context-plus';
import { Collapse } from '../../components';
import { useMaskConfig } from '../../hooks/mask-context';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';

type NumberingLogicFormValue = PathsOf<NumberingLogic, 2>;

type NumberingLogicOverlayControllerProps = MaskControllerProps;

const DEFAULT_VALUES = {};

export function NumberingLogicOverlayController(
  props: NumberingLogicOverlayControllerProps
) {
  const { amplitudeEntryPoint } = props;

  const { t } = useTranslation();

  const [mutate] = useDataMutation<NumberingLogic, EMode.upsert>({
    entity: Entities.numberingLogic,
    mutationType: EMode.upsert,
    responseData: {
      id: null,
      firstNumber: null,
      lastNumber: null,
    },
    onCompleted: (data) => {
      props.onAfterSave?.(data);
      sendAmplitudeData(EventType.SaveNumberingLogic, {
        entryPoint: amplitudeEntryPoint,
      });
    },
  });

  const mask = useMaskConfig(props);

  useEffect(() => {
    if (!mask.isCreateMode) {
      sendAmplitudeData(EventType.EditNumberingLogic, {
        entryPoint: amplitudeEntryPoint,
      });
    }
  }, [amplitudeEntryPoint, mask, mask.isCreateMode, props.id]);

  const request = useNumberingLogicRequestData(Number(mask.id));

  const customRules = useCallback(
    (data) => {
      if (data.lastNumber < data.firstNumber) {
        return {
          firstNumber: {
            message: t('ERROR.FIRST_NUMBER_HIGHER_THAN_LAST_NUMBER'),
            type: 'customValidation',
          },
          lastNumber: {
            message: t('ERROR.FIRST_NUMBER_HIGHER_THAN_LAST_NUMBER'),
            type: 'customValidation',
          },
        };
      }
      return true;
    },
    [t]
  );

  const overlay = useMaskOverlay<NumberingLogicFormValue>({
    ...props,
    request,
    newEntityData: DEFAULT_VALUES,
    mask,
    getSubTitle: (x) => t(`NUMBERINGLOGIC.${x.id}`),
    customRules,
    id: Number(mask.id),
    skipEventQuery: true,
  });

  const { formState, getValues } = overlay.form;

  const handleSubmit = useEventCallback(
    async (values: NumberingLogicFormValue) => {
      const update = pick(values, [
        'id',
        ...Object.keys(formState.dirtyFields),
      ]);
      await mutate(update);
    }
  );

  const firstNumber = getValues('firstNumber');
  const lastNumber = getValues('lastNumber');

  const disabled = firstNumber > lastNumber;

  return (
    <OverlayController<NumberingLogicFormValue>
      {...overlay}
      onSubmit={handleSubmit}
      disableDelete
      hideDeleteButton
      submitButtonProps={{
        ...overlay.submitButtonProps,
        disabled,
        disableReason: disabled
          ? t('ERROR.FIRST_NUMBER_HIGHER_THAN_LAST_NUMBER')
          : undefined,
      }}
    >
      <NumberingLogicMaskContent />
    </OverlayController>
  );
}

function NumberingLogicMaskContent() {
  const { t } = useTranslation();

  const { register } = useFormContextPlus<NumberingLogicFormValue>();

  return (
    <Collapse title={t('COMMON.GENERAL')} defaultOpen>
      <Stack gap="1rem">
        <Grid container spacing="1rem">
          <Grid item xs={12} sm={6}>
            <LabeledInput
              label={t('NUMBERINGLOGIC.FIRST_NUMBER')}
              type="number"
              {...register('firstNumber')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <LabeledInput
              label={t('NUMBERINGLOGIC.LAST_NUMBER')}
              type="number"
              {...register('lastNumber')}
            />
          </Grid>
        </Grid>
      </Stack>
    </Collapse>
  );
}

export const useNumberingLogicRequestData = (id: string | number) => {
  return useMemo<DataRequest>(() => {
    const filter = [{ id: { $eq: id } }];
    const data: NumberingLogic<EMode.query> = {
      id: null,
      firstNumber: null,
      lastNumber: null,
      step: null,
      __typename: null,
    };
    return {
      entity: Entities.numberingLogic,
      data,
      filter,
      completeDataResponse: true,
    };
  }, [id]);
};
